import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFavouritesStore, useSpaceStore } from "../../stores";
import type { CelestialObject, StarSystem } from "../../types";
import "./SearchBar.css";

const MAX_RESULTS = 10;

interface ScoredResult {
	obj: CelestialObject;
	score: number;
}

function searchSystems(systems: StarSystem[], query: string): ScoredResult[] {
	const lower = query.toLowerCase();
	const results: ScoredResult[] = [];

	for (const system of systems) {
		const starName = system.star.name.toLowerCase();
		const systemName = system.name.toLowerCase();

		// Match on star name or system name
		if (starName.includes(lower) || systemName.includes(lower)) {
			const prefixMatch = starName.startsWith(lower) || systemName.startsWith(lower);
			results.push({
				obj: { type: "star", data: system },
				score: prefixMatch ? 2 : 1,
			});
		}

		// Match on planet names
		for (const planet of system.planets) {
			const planetName = planet.name.toLowerCase();
			if (planetName.includes(lower)) {
				results.push({
					obj: { type: "planet", data: planet, system },
					score: planetName.startsWith(lower) ? 2 : 1,
				});
			}
		}
	}

	return results.sort((a, b) => b.score - a.score).slice(0, MAX_RESULTS);
}

function formatDistance(ly: number): string {
	return ly < 1 ? `${(ly * 1000).toFixed(1)} mly` : `${ly.toFixed(2)} ly`;
}

function ResultItem({ obj, onSelect }: { obj: CelestialObject; onSelect: () => void }) {
	if (obj.type === "star") {
		const system = obj.data;
		const planetCount = system.planets.length;
		return (
			<button type="button" className="search-result" onClick={onSelect}>
				<span className="result-icon">⊛</span>
				<div className="result-info">
					<span className="result-name">{system.star.name}</span>
					<span className="result-meta">
						{formatDistance(system.star.distanceLightYears)} · {planetCount} planet
						{planetCount !== 1 ? "s" : ""}
					</span>
				</div>
				<span className="result-type">star</span>
			</button>
		);
	}

	const { data: planet, system } = obj;
	return (
		<button type="button" className="search-result" onClick={onSelect}>
			<span className="result-icon" style={{ color: planet.color }}>
				●
			</span>
			<div className="result-info">
				<span className="result-name">{planet.name}</span>
				<span className="result-meta">Host: {system.star.name}</span>
			</div>
			<span className="result-type">planet</span>
		</button>
	);
}

export function SearchBar() {
	const [query, setQuery] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const isOpen = useSpaceStore((s) => s.isSearchOpen);
	const setIsOpen = useSpaceStore((s) => s.setSearchOpen);
	const systems = useSpaceStore((s) => s.systems);
	const selectObject = useSpaceStore((s) => s.selectObject);
	const enterSystemView = useSpaceStore((s) => s.enterSystemView);

	// Keyboard shortcut: Cmd+K to open, ESC to close
	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if ((e.metaKey || e.ctrlKey) && e.key === "k") {
				e.preventDefault();
				setIsOpen(true);
			}
			if (e.key === "Escape") {
				setIsOpen(false);
			}
		}

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [setIsOpen]);

	// Focus input when opened, clear query when closed
	useEffect(() => {
		if (isOpen) {
			setTimeout(() => inputRef.current?.focus(), 100);
		} else {
			setQuery("");
		}
	}, [isOpen]);

	const favourites = useFavouritesStore((s) => s.favourites);

	const results = useMemo(() => {
		if (!query.trim()) return [];
		return searchSystems(systems, query);
	}, [query, systems]);

	// Resolve favourite IDs to CelestialObjects (shown when query is empty)
	const favouriteItems = useMemo(() => {
		if (favourites.size === 0) return [];
		const items: CelestialObject[] = [];
		for (const system of systems) {
			if (favourites.has(system.id)) {
				items.push({ type: "star", data: system });
			}
			for (const planet of system.planets) {
				if (favourites.has(planet.id)) {
					items.push({ type: "planet", data: planet, system });
				}
			}
		}
		return items;
	}, [favourites, systems]);

	function handleSelect(obj: CelestialObject) {
		if (obj.type === "star") {
			enterSystemView(obj.data);
		} else {
			enterSystemView(obj.system);
			setTimeout(() => selectObject(obj), 50);
		}

		setIsOpen(false);
	}

	function close() {
		setIsOpen(false);
	}

	return (
		<>
			<button type="button" className="search-trigger" onClick={() => setIsOpen(true)}>
				<svg
					width="18"
					height="18"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<title>Search</title>
					<circle cx="11" cy="11" r="8" />
					<path d="M21 21l-4.35-4.35" />
				</svg>
				<span>Search stars & planets...</span>
				<kbd>⌘K</kbd>
			</button>

			<AnimatePresence>
				{isOpen && (
					<>
						<motion.div
							className="search-backdrop"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							onClick={close}
						/>
						<motion.div
							className="search-modal"
							initial={{ opacity: 0, scale: 0.95, y: -20 }}
							animate={{ opacity: 1, scale: 1, y: 0 }}
							exit={{ opacity: 0, scale: 0.95, y: -20 }}
							transition={{ type: "spring", damping: 25 }}
						>
							<div className="search-input-wrapper">
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
								>
									<title>Search</title>
									<circle cx="11" cy="11" r="8" />
									<path d="M21 21l-4.35-4.35" />
								</svg>
								<input
									ref={inputRef}
									type="text"
									className="search-input"
									placeholder="Search for stars, planets..."
									value={query}
									onChange={(e) => setQuery(e.target.value)}
								/>
								<button type="button" className="search-esc-btn" onClick={close}>
									ESC
								</button>
							</div>

							{results.length > 0 && (
								<div className="search-results">
									{results.map(({ obj }) => (
										<ResultItem
											key={obj.type === "star" ? `star-${obj.data.id}` : `planet-${obj.data.id}`}
											obj={obj}
											onSelect={() => handleSelect(obj)}
										/>
									))}
								</div>
							)}

							{!query && favouriteItems.length > 0 && (
								<div className="search-results">
									<div className="search-section-label">Favourites</div>
									{favouriteItems.map((obj) => (
										<ResultItem
											key={
												obj.type === "star"
													? `fav-star-${obj.data.id}`
													: `fav-planet-${obj.data.id}`
											}
											obj={obj}
											onSelect={() => handleSelect(obj)}
										/>
									))}
								</div>
							)}

							{query && results.length === 0 && (
								<div className="search-empty">
									<p>No celestial objects found</p>
								</div>
							)}
						</motion.div>
					</>
				)}
			</AnimatePresence>
		</>
	);
}
